import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

// local imports & assets
import calendarLoading from 'assets/icons/loading-calendar.png'
import { ICoiffeurSalonFilteredReq, IFilteredCoiffeurSalonList } from 'types/CoiffeurType'
import useDidMountEffect from 'utils/useDidMountEffect'
import DateUtil from 'utils/DateUtil'
import AuthUtil from 'utils/AuthUtil'
import useGetSalonLocationList from '../hooks/use-get-salon-locations'
import useGetCoiffeurSalonList from '../hooks/use-get-coiffeur-salon-list'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import CoiffeurSalonCard from './CoiffeurSalonCard'
import CoiffeurSalonFilterForm from './CoiffeurSalonFilterForm'
import CoiffeurSalonSearch from './CoiffeurSalonSearch'
import CoiffeurSearchModal from '../coiffeur-search-modal/CoiffeurSearchModal'
import { Divider, FormLayoutWrapper, Line, SalonCardList } from './styled'

/* filtered date - default */
const today = DateUtil.getStartOfDay(new Date())

export default function CoiffeurSalonList() {
  const [locationIds, setLocationIds] = useState<number[] | []>([])

  /* Get Current logged employee infos - (location id) */
  const { data: locationList, isLoading } = useGetSalonLocationList()

  useEffect(() => {
    const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId || 0
    const empLocationIsExist = locationList?.map((o: any) => o.id).includes(employeeLocation)
    setLocationIds(() => (empLocationIsExist ? [employeeLocation] : []))
    setValue('locationIds', empLocationIsExist ? [employeeLocation] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList])

  /*  Search and filter from coiffeur salon list */
  const [searchText, setSearchText] = useState('')
  const [filteredSalon, setFilteredSalon] = useState<IFilteredCoiffeurSalonList[] | []>([])

  const [formModal, setFormModal] = useState(false)
  const [showFormModal, setShowFormModal] = useState(false)
  const [selectedSalon, setSelectedSalon] = useState<IFilteredCoiffeurSalonList | undefined>()
  const openFormModal = (datum: IFilteredCoiffeurSalonList) => {
    setSelectedSalon(datum)
    setFormModal(true)
    setShowFormModal(true)
  }

  /* Filter form default values */
  const defaultValues: ICoiffeurSalonFilteredReq = {
    locationIds: locationIds,
    reservationDate: today,
    isOnlyUserGender: true,
  }

  /* Filter form state  */
  const { setValue, control, reset, handleSubmit, formState, watch } = useForm<ICoiffeurSalonFilteredReq>({
    defaultValues,
  })
  const filteredDate = watch('reservationDate') || today

  /* Get Coiffeur Salon List Get Coiffeur Salon Specialties - Locations | Custom Hooks */
  const { getSalonList, coiffeurSalons, loading } = useGetCoiffeurSalonList()

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1

  /* Handle Form Submit */
  const onSubmit = (data: ICoiffeurSalonFilteredReq) => {
    getSalonList(data)
  }

  /* Handle Reset Filter Form */
  const handleResetForm = () => {
    reset(
      { locationIds: locationIds, reservationDate: today },
      { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false }
    )
    !!submitCount && onSubmit(defaultValues)
    setSearchText('')
  }

  /* Get Coiffeur Salon List on initial page load  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDidMountEffect(() => onSubmit(defaultValues), [isLoading])

  /* Render coiffeurSalons or filtered Coiffeur Salons list */
  const renderList = searchText ? filteredSalon : coiffeurSalons

  /* Handle Loading Status */
  if (loading || isLoading) return <ListLoader />

  return (
    <div className="campus-services">
      {/* Filter Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <CoiffeurSalonSearch
            salons={coiffeurSalons}
            setData={setFilteredSalon}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          <Line className="pi pi-ellipsis-h" />

          <CoiffeurSalonFilterForm
            control={control}
            locationList={locationList}
            disableResetBtn={disableResetBtn}
            disableSubmitBtn={disableSubmitBtn}
            handleReset={handleResetForm}
          />
        </FormLayoutWrapper>
      </form>

      <Divider />

      {/* Card List */}
      {loading ? (
        <ListLoader />
      ) : (
        <SalonCardList>
          {!!renderList?.length ? (
            renderList?.map((salon) => <CoiffeurSalonCard openModal={openFormModal} key={salon.id} salon={salon} />)
          ) : (
            <NoContent />
          )}
        </SalonCardList>
      )}

      {showFormModal && selectedSalon ? (
        <CoiffeurSearchModal
          filteredDate={filteredDate}
          salon={selectedSalon}
          formModal={formModal}
          setFormModal={setFormModal}
          setShowModal={setShowFormModal}
        />
      ) : null}
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}
